import { useEffect, useSyncExternalStore } from 'react'
import { authFetch } from '@/services/apiClient'
import { useAuthStore } from '@/store/authStore'

// ตะกร้าสินค้า — เก็บที่ server (ตาราง t_cart_item) ตามบัญชี ข้ามเครื่องได้ · แยก 2 ตะกร้า: คอร์สออนไลน์ (online) / หนังสือ (book)
// ชื่อ รูป ราคา มาจาก API สดทุกครั้ง · ราคา/สต๊อก/กติกาตรวจซ้ำที่ API ตอนเปิดหน้าตะกร้า (POST /api/cart/preview) และตอนสร้างคำสั่งซื้อ
// Onsite และแพ็กเกจ Employer ไม่มีตะกร้า · ชำระเงินสำเร็จแล้ว server ลบรายการที่ซื้อออกจากตะกร้าให้เอง (เรียก refresh เพื่อดึงใหม่)
//
// ตะกร้าเดิมที่เคยอยู่ใน localStorage ของเบราว์เซอร์ (รุ่นก่อนมีตารางตะกร้า): ย้ายขึ้น server ให้ครั้งเดียวตอนโหลดตะกร้าครั้งแรกหลังอัปเดต

export type CartKind = 'online' | 'book'

export interface CartLine {
  cartItemId: number
  kind: CartKind
  refId: number          // course_id หรือ book_id
  quantity: number       // ออนไลน์: บุคคลทั่วไป = 1 · บริษัท = จำนวนพนักงาน · หนังสือ = จำนวนเล่ม
  title: string
  slug: string | null
  price: number          // ราคาต่อหน่วยตอนนี้ (แสดงชั่วคราว) — ยอดจริงมาจาก preview
  image: string | null
  subtitle: string | null
  available: boolean     // false = สินค้าถูกปิดขาย/ลบไปแล้ว
}

export const MAX_BOOK_QUANTITY = 50       // เท่ากับ MAX_BOOK_QUANTITY ฝั่ง API
export const MAX_ONLINE_QUANTITY = 1000   // เท่ากับ MAX_ONLINE_RECIPIENTS ฝั่ง API (จำนวนพนักงานต่อคอร์สของบริษัท)

interface ApiCartItem {
  cart_item_id: number
  kind: CartKind
  ref_id: number
  quantity: number
  title: string
  slug: string | null
  image: string | null
  subtitle: string | null
  price: number
  available: boolean
}

const EMPTY: CartLine[] = []
const fromApi = (items: ApiCartItem[]): CartLine[] =>
  items.map((i) => ({
    cartItemId: i.cart_item_id, kind: i.kind, refId: i.ref_id, quantity: i.quantity, title: i.title, slug: i.slug,
    price: i.price, image: i.image, subtitle: i.subtitle, available: i.available,
  }))

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(path, init)
  let json: { success: boolean; data?: T; error?: string; message?: string }
  try {
    json = await res.json()
  } catch {
    throw new Error('เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ')
  }
  if (!json.success || json.data === undefined) throw new Error(json.error || json.message || 'ทำรายการไม่สำเร็จ')
  return json.data
}

// ── store กลาง (ทุกคอมโพเนนต์ที่เรียก useCart ใช้ข้อมูลชุดเดียวกัน) ─────────────────
interface Store { customerId: number | null; lines: CartLine[]; loaded: boolean }
let store: Store = { customerId: null, lines: EMPTY, loaded: false }
const listeners = new Set<() => void>()
const setStore = (patch: Partial<Store>) => {
  store = { ...store, ...patch }
  listeners.forEach((l) => l())
}
const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

// ย้ายตะกร้าเก่าใน localStorage ขึ้น server (ครั้งเดียว) — ลบของในเครื่องเมื่อ server รับแล้วเท่านั้น (เน็ตหลุดจะลองใหม่รอบหน้า)
async function importLegacyCart(customerId: number) {
  const key = `jos_cart_v1_${customerId}`
  const raw = (() => { try { return localStorage.getItem(key) } catch { return null } })()
  if (!raw) return
  let items: { item_type: string; ref_id: number; quantity: number }[] = []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      items = parsed
        .filter((l) => l && (l.kind === 'online' || l.kind === 'book') && Number.isInteger(l.refId))
        .map((l) => ({ item_type: l.kind === 'book' ? 'BOOK' : 'COURSE', ref_id: l.refId, quantity: Number(l.quantity) || 1 }))
    }
  } catch { /* ข้อมูลเสีย → ทิ้ง */ }
  if (items.length > 0) {
    await call('/cart/import', { method: 'POST', body: JSON.stringify({ items }) })
  }
  try { localStorage.removeItem(key) } catch { /* ข้าม */ }
}

let loadingFor: number | null = null
export async function ensureCartLoaded(customerId: number | null): Promise<void> {
  if (customerId === null) {
    if (store.customerId !== null || store.lines.length > 0) setStore({ customerId: null, lines: EMPTY, loaded: false })
    return
  }
  if (store.customerId === customerId && (store.loaded || loadingFor === customerId)) return
  setStore({ customerId, lines: EMPTY, loaded: false })
  loadingFor = customerId
  try {
    try { await importLegacyCart(customerId) } catch { /* ย้ายไม่สำเร็จ — ดึงตะกร้าบน server ต่อ ตะกร้าเก่ายังอยู่ในเครื่องรอลองใหม่ */ }
    const data = await call<{ items: ApiCartItem[] }>('/cart')
    if (store.customerId === customerId) setStore({ lines: fromApi(data.items), loaded: true })
  } catch {
    if (store.customerId === customerId) setStore({ loaded: true })
  } finally {
    if (loadingFor === customerId) loadingFor = null
  }
}

const refresh = async () => {
  const customerId = store.customerId
  if (customerId === null) return
  try {
    const data = await call<{ items: ApiCartItem[] }>('/cart')
    if (store.customerId === customerId) setStore({ lines: fromApi(data.items), loaded: true })
  } catch { /* ข้าม — ยังแสดงของเดิม */ }
}

export type CartResult = { ok: true } | { ok: false; error: string }
const errorOf = (e: unknown): CartResult => ({ ok: false, error: e instanceof Error ? e.message : 'ทำรายการไม่สำเร็จ' })

const add = async (input: { kind: CartKind; refId: number; quantity?: number }): Promise<CartResult> => {
  try {
    const data = await call<{ items: ApiCartItem[] }>('/cart/items', {
      method: 'POST',
      body: JSON.stringify({ item_type: input.kind === 'book' ? 'BOOK' : 'COURSE', ref_id: input.refId, quantity: input.quantity ?? 1 }),
    })
    setStore({ lines: fromApi(data.items), loaded: true })
    return { ok: true }
  } catch (e) {
    return errorOf(e)
  }
}

// แก้จำนวน: แสดงผลทันที แล้วยืนยันกับ server — server ปฏิเสธ (เช่น เกินสต๊อก) จะย้อนกลับเป็นค่าเดิมพร้อมข้อความ
const setQuantity = async (kind: CartKind, refId: number, quantity: number): Promise<CartResult> => {
  const before = store.lines
  const line = before.find((l) => l.kind === kind && l.refId === refId)
  if (!line) return { ok: false, error: 'ไม่พบรายการในตะกร้า' }
  const q = Math.min(kind === 'book' ? MAX_BOOK_QUANTITY : MAX_ONLINE_QUANTITY, Math.max(1, Math.floor(quantity) || 1))
  setStore({ lines: before.map((l) => (l === line ? { ...l, quantity: q } : l)) })
  try {
    const data = await call<{ items: ApiCartItem[] }>(`/cart/items/${line.cartItemId}`, { method: 'PATCH', body: JSON.stringify({ quantity: q }) })
    setStore({ lines: fromApi(data.items) })
    return { ok: true }
  } catch (e) {
    setStore({ lines: before })
    return errorOf(e)
  }
}

const remove = async (kind: CartKind, refId: number): Promise<CartResult> => {
  const before = store.lines
  const line = before.find((l) => l.kind === kind && l.refId === refId)
  if (!line) return { ok: true }
  setStore({ lines: before.filter((l) => l !== line) })
  try {
    const data = await call<{ items: ApiCartItem[] }>(`/cart/items/${line.cartItemId}`, { method: 'DELETE' })
    setStore({ lines: fromApi(data.items) })
    return { ok: true }
  } catch (e) {
    setStore({ lines: before })
    return errorOf(e)
  }
}

export function useCart() {
  const customerId = useAuthStore((s) => s.customerId)
  useEffect(() => { void ensureCartLoaded(customerId) }, [customerId])
  const snapshot = useSyncExternalStore(subscribe, () => store, () => store)
  const mine = snapshot.customerId === customerId
  const lines = mine ? snapshot.lines : EMPTY
  return {
    lines,
    loaded: mine && snapshot.loaded,
    online: lines.filter((l) => l.kind === 'online'),
    books: lines.filter((l) => l.kind === 'book'),
    count: lines.length,
    has: (kind: CartKind, refId: number) => lines.some((l) => l.kind === kind && l.refId === refId),
    add,
    setQuantity,
    remove,
    refresh,
  }
}

// ── ตรวจตะกร้ากับ API ───────────────────────────────────────────────
export interface CartPreviewLine {
  item_type: string
  ref_id: number
  ok: boolean
  error: string | null
  item_name?: string
  unit_price?: number
  quantity?: number
  subtotal?: number
  is_free?: boolean
}

export interface CartPreview {
  items: CartPreviewLine[]
  cart_error: string | null
  subtotal: number
  discount: number
  final_amount: number
  coupon: { code: string; discount_type: 'PERCENT' | 'FIXED' } | null
  coupon_error: string | null
  can_checkout: boolean
}

export const toOrderItems = (lines: CartLine[]) =>
  lines.map((l) => ({ item_type: l.kind === 'book' ? 'BOOK' : 'COURSE', ref_id: l.refId, quantity: l.quantity }))

export const previewCart = (lines: CartLine[], couponCode?: string | null): Promise<CartPreview> =>
  call<CartPreview>('/cart/preview', {
    method: 'POST',
    body: JSON.stringify({ items: toOrderItems(lines), coupon_code: couponCode || undefined }),
  })
